import React, { useEffect, useRef, useState } from "react";
import "./Dropdown.css";

// Stand-in for a native <select>. The browser draws that option list itself, so
// it ignores the app's palette entirely and renders dark on a phone set to dark
// mode. This keeps the `onChange({ target: { name, value } })` shape a <select>
// has, so a screen can swap over without touching its change handler.
// A native select gives type-ahead for free; a long list needs a search box to
// stay usable without it.
// An option may carry `fieldLabel` when the closed field has no room for the
// full label: a country picker lists "India (+91)" but the field shows "+91".
const SEARCHABLE_FROM = 8;

export default function Dropdown({
    name,
    value,
    onChange,
    options = [],
    placeholder = "Select",
    disabled = false,
    className = "",
    searchable,
    searchPlaceholder = "Search",
}) {

    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [openUpwards, setOpenUpwards] = useState(false);
    const [query, setQuery] = useState("");

    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const searchRef = useRef(null);

    const canSearch = searchable ?? options.length >= SEARCHABLE_FROM;

    const visibleOptions = query.trim() === ""
        ? options
        : options.filter(option =>
            String(option.label).toLowerCase().includes(query.trim().toLowerCase()));

    const selected = options.find(option => String(option.value) === String(value));
    const selectedIndex = visibleOptions.findIndex(option => String(option.value) === String(value));

    // Tapping anywhere else, or Escape, closes the list.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
                buttonRef.current?.focus();
            }
        };

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen]);

    const open = () => {
        if (disabled || options.length === 0) {
            return;
        }

        // Near the bottom of the screen there is no room below the field, which
        // is where these pickers usually sit.
        const box = buttonRef.current?.getBoundingClientRect();
        if (box) {
            const below = window.innerHeight - box.bottom;
            setOpenUpwards(below < 220 && box.top > below);
        }

        setQuery("");
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setIsOpen(true);
    };

    const choose = (option) => {
        onChange({ target: { name, value: option.value } });
        setIsOpen(false);
        buttonRef.current?.focus();
    };

    const onButtonKeyDown = (event) => {
        if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            isOpen ? setIsOpen(false) : open();
        }
    };

    const onListKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex(index => Math.min(index + 1, visibleOptions.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (visibleOptions[activeIndex]) {
                choose(visibleOptions[activeIndex]);
            }
        }
    };

    return (
        <div className={`dropdown ${className} ${disabled ? "dropdown--disabled" : ""}`} ref={rootRef}>
            <button
                type="button"
                className={`dropdown-field ${isOpen ? "is-open" : ""}`}
                ref={buttonRef}
                onClick={() => (isOpen ? setIsOpen(false) : open())}
                onKeyDown={onButtonKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className={selected ? "dropdown-value" : "dropdown-placeholder"}>
                    {selected ? (selected.fieldLabel ?? selected.label) : placeholder}
                </span>
                <span className="dropdown-caret" aria-hidden="true" />
            </button>

            {isOpen && (
                <div
                    className={`dropdown-panel ${openUpwards ? "dropdown-panel--up" : ""}`}
                    onKeyDown={onListKeyDown}
                >
                    {canSearch && (
                        <input
                            type="text"
                            className="dropdown-search"
                            placeholder={searchPlaceholder}
                            value={query}
                            ref={searchRef}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            onChange={(event) => {
                                setQuery(event.target.value);
                                setActiveIndex(0);
                            }}
                        />
                    )}

                    <ul
                        className="dropdown-list"
                        role="listbox"
                        tabIndex={-1}
                        ref={(node) => { if (!canSearch) node?.focus(); }}
                    >
                        {visibleOptions.length === 0 && (
                            <li className="dropdown-empty">No matches</li>
                        )}

                        {visibleOptions.map((option, index) => {
                            const isSelected = String(option.value) === String(value);
                            return (
                                <li
                                    key={option.value}
                                    role="option"
                                    aria-selected={isSelected}
                                    className={`dropdown-option ${isSelected ? "is-selected" : ""} ${index === activeIndex ? "is-active" : ""}`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => choose(option)}
                                >
                                    <span>{option.label}</span>
                                    {isSelected && <span className="dropdown-tick" aria-hidden="true">✓</span>}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
